import { useState,Fragment,useEffect, useRef } from 'react';
import styles from './style.module.css';
import useApi from '../../services/useApi';
import Sidebar from './Sidebar';
import SingleResult from './SingleResult';
import BarChart from './BarChart';
import Portal from '../../utils/Portal';
import OutputSkeleton from './OutputSkeleton';
import BatchUpload from './BatchUpload';
import Confusion from './Confusion';
import ThresholdSlider from './ThresholdSlider';

export default function Playground(){
    const bodyRef=useRef(null);
    const [isFirstLoad,setIsFirstLoad]=useState(true);
    const [isListLoad,setIsListLoad]=useState(true);
    const [isRunning,setIsRunning]=useState(false);
    const [modelList,setModelList]=useState([]);
    const [checkedModels,setCheckedModels]=useState([]);

    const inputRef=useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [inputFocus,setInputFocus]=useState(false);

    const [thresValue,setThresValue]=useState(0.965);

    const [results, setResults] = useState(() => {
        const saved = localStorage.getItem('playgroundResults');
        return saved ? JSON.parse(saved) : [];
    });
    const [expandedRow, setExpandedRow] = useState(null);
    const [isBatchSelector,setIsBatchSelector]=useState(false);
    const batchSelectorRef=useRef(null);
    const [batchSelectorPos,setBatchSelectorPos]=useState({top:0,left:0});
    const batchDropdownRef=useRef(null);
    const [isBatchUpload,setIsBatchUpload]=useState(false);
    const [batchList,setBatchList]=useState([]);
    const [batchId,setBatchId]=useState('');

    useEffect(()=>{
        const textarea=inputRef.current;

        if(!textarea)return;

        if(inputFocus&&inputValue.split('\n').length>1){
            textarea.style.height='auto';
            textarea.style.height=Math.min(textarea.scrollHeight,200)+'px';
        }
        else if(inputFocus&&inputValue.split('\n').length===1){
            textarea.style.height=44+'px';
        }
    },[inputValue,inputFocus])

    useEffect(()=>{
        useApi.get('backboard/model/playground?userId='+ JSON.parse(localStorage.getItem("account")).id).then((res)=>{
            useApi.get('backboard/model/dashboard').then((resAll)=>{
                setModelList([...res.data,...resAll.data.map(item=>({...item,modelName:item.displayName}))]);
                setCheckedModels([...res.data.map(item=>item.modelId),...resAll.data.map(item=>item.modelId)]);
                setIsListLoad(false);
            }).catch(function (thrown) {
            });
        }).catch(function (thrown) {
        });

        useApi.get('backboard/datasets/dashboards').then((res)=>{
            setBatchList(prev=>[...prev,...res.data]);
        }).catch(function (thrown) {
        });

        useApi.get('backboard/datasets?userId='+ JSON.parse(localStorage.getItem("account")).id).then((res)=>{
            setBatchList(prev=>[...prev,...res.data]);
        }).catch(function (thrown) {
        });

        // useApi.get('backboard/datasets/dashboards').then((res)=>{
        //     setTestSetList(res.data);
        //     setCheckedTests(res.data.map(item=>item.datasetId));
        // }).catch(function (thrown) {
        // });

        const handleClickOutside=(e)=>{
            if(batchDropdownRef.current&&!batchDropdownRef.current.contains(e.target)){
                setIsBatchSelector(false);
            }
        }
        document.addEventListener('mousedown',handleClickOutside);
        return ()=>document.removeEventListener('mousedown',handleClickOutside);
    },[])

    useEffect(()=>{
        if(bodyRef.current){
            bodyRef.current.scrollTo({
                top:bodyRef.current.scrollHeight,
                behavior:isFirstLoad?'auto':'smooth'
            })
        }
        if(isFirstLoad){
            setIsFirstLoad(false);
        }
    },[results,isRunning])

    useEffect(() => {
      localStorage.setItem('playgroundResults', JSON.stringify(results));
    }, [results]);

    const handleKeyDown=(e)=>{
        if(e.key==='Enter'){
            if(e.shiftKey){
                return;
            }else{
                e.preventDefault();
                handleRun();
            }
        }

        if(e.key==='Escape'){
            inputRef.current.blur();
        }
    }

    const handleBlur=()=>{
        setInputFocus(false);
        inputRef.current.style.height=44+'px';
    }

    const handleRun = () => {
        if(!inputValue.trim())return;
        setIsRunning(true);
        useApi.post('backboard/tests/single',{
            'userId':JSON.parse(localStorage.getItem("account")).id,
            'message':inputValue,
            'modelIds':checkedModels
        }).then((res)=>{
            setResults(prev => [...prev, {
                type:'single',
                input:res.data.message,
                threshold:thresValue,
                table:res.data.results.map(item=>({
                    ...item,
                    modelName:modelList.find(m=>m.modelId===item.modelId).modelName,
                    predictionResult:item.predictions[1]>thresValue?'spam':'non-spam',
                }))
            }]);
            setIsRunning(false);
        })
        .catch(async (err) => {
            setIsRunning(false);
        });
    };

    const handleRunBatch = (datasetId) => {
        useApi.post('backboard/tests/batch',{
            userId:JSON.parse(localStorage.getItem("account")).id,
            modelIds:checkedModels,
            datasetIds:[datasetId],
            "targetParameter": "threshold",
            "targetValue": thresValue,
        })
        .then(res=>{
            setBatchId(res.data.batchId);
            setIsRunning(true);
        })
        .catch(async (err) => {
        });

    };

    const pollingRef = useRef(null);

    useEffect(()=>{
        if(batchId===''||!batchId)return;
        
        const poll=()=>{
            useApi.get('backboard/batches/'+batchId + '/details').then(res=>{
                const allCompleted = res.data.testList.every(item => item.result !== null);
      
                if (allCompleted) {
                    clearInterval(pollingRef.current);

                    const accuracyArr = [];
                    const confusionArr = [];

                    res.data.testList.forEach(test => {
                        const model = modelList.find(m => m.modelId === test.modelId);
                        if (!model || !test.result) return;
                    
                        const { accuracy, confusionDetail } = test.result;
                    
                        if (accuracy !== undefined) {
                          accuracyArr.push({
                            modelName: model.modelName,
                            accuracy
                          });
                        }
                    
                        if (confusionDetail !== undefined) {
                          confusionArr.push({
                            modelName: model.modelName,
                            confusionDetail
                          });
                        }
                    });
                    
                    setResults(prev=>[...prev,{
                      type: 'batch',
                      accuracy: accuracyArr,
                      confusion: confusionArr,
                      ...res.data
                    }]);
                    setBatchId('');
                    setIsRunning(false);
                }
            })
        }
      
        poll();
        pollingRef.current = setInterval(poll, 2000);
      
        return () => {
          clearInterval(pollingRef.current);
        };
    },[batchId])

    const handleClear = () => {
        localStorage.removeItem('playgroundResults');
        setResults([]);
    };

    const handleRefreshlList=()=>{
        setIsListLoad(true);
        useApi.get('backboard/model/playground?userId='+ JSON.parse(localStorage.getItem("account")).id).then((res)=>{
            useApi.get('backboard/model/dashboard').then((resAll)=>{
                setModelList([...res.data,...resAll.data.map(item=>({...item,modelName:item.displayName}))]);
                setCheckedModels([...res.data.map(item=>item.modelId),...resAll.data.map(item=>item.modelId)]);
                setIsListLoad(false);
            }).catch(function (thrown) {
            });
        }).catch(function (thrown) {
        });
    }

    const toggleDetail = (index) => {
      setExpandedRow(expandedRow === index ? null : index);
    };

    useEffect(()=>{
        if(isBatchSelector===false)return;
        const bodyRefCurrent=bodyRef.current;
        if(!bodyRefCurrent)return;

        handleBatchSelector();
        bodyRefCurrent.addEventListener('scroll',handleBatchSelector);
        window.addEventListener('resize',handleBatchSelector);
        return()=>{
            bodyRefCurrent.removeEventListener('scroll',handleBatchSelector);
            window.removeEventListener('resize',handleBatchSelector);
        }
    },[isBatchSelector])

    const handleBatchSelector=()=>{
        if(isBatchSelector===false)return;
        const rect=batchSelectorRef.current.getBoundingClientRect();
        setBatchSelectorPos({top:rect.bottom+window.scrollY+6,left:rect.right+window.scrollX-200})
    }

    const handleRefreshlBatch=()=>{
        setIsBatchUpload(false)
        useApi.get('backboard/datasets?userId='+ JSON.parse(localStorage.getItem("account")).id).then((res)=>{
            setBatchList(prev=>{
                const map= new Map();
                prev.forEach(item=>map.set(item.datasetId,item))
                res.data.forEach(item=>map.set(item.datasetId,item))
                return Array.from(map.values());
            });
            setIsBatchSelector(true);
        }).catch(function (thrown) {
        });
    }

    return (
        <div className={styles['wrap']}>
            <Sidebar
            isLoad={isListLoad}
            modelList={modelList}
            checkedModels={checkedModels}
            setCheckedModels={setCheckedModels}
            onChange={handleRefreshlList}
            />
                <div ref={bodyRef} className={styles['body']}>
                <header>
                    <span>Playground</span>
                    {/* <button>Export to CSV</button> */}
                </header>
                <div className={styles.container}>
                    <header>
                        <span>Input</span>
                    </header>
                    <div className={styles.inputArea}>
                        <textarea
                        ref={inputRef}
                        className={styles.textInput}
                        value={inputValue}
                        placeholder="텍스트를 입력하세요"
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        onFocus={()=>setInputFocus(true)}
                        />
                        <div className={styles['input-tools']}>
                            <ThresholdSlider
                            className={styles['thres-slider']}
                            thresValue={thresValue}
                            setThresValue={setThresValue}
                            />
                            <button 
                            className={styles['batch-btn']}
                            ref={batchSelectorRef} 
                            onClick={()=>setIsBatchSelector(!isBatchSelector)}>
                                Select batch
                                <hr/>
                                {isBatchSelector&&<Portal><div 
                                ref={batchDropdownRef}
                                className={styles['batch-selector']}
                                style={{
                                    top:batchSelectorPos.top,
                                    left:batchSelectorPos.left,
                                }}>
                                    <span onClick={()=>setIsBatchUpload(true)}>Add batch test</span>
                                    {batchList.map(item=><>
                                        <span onClick={()=>handleRunBatch(item.datasetId)}>{item.datasetName}</span>
                                    </>)}
                                </div></Portal>}
                            </button>
                            <button className={styles['run-btn']} onClick={handleRun}>Run</button>
                        </div>
                    </div>
                </div>
                <div className={styles.container}>
                    <header>
                        <span>Output</span>
                        <button className={styles['btn-clear']} onClick={handleClear}>Clear</button>
                    </header>
                    <div className={styles.outputArea}>
                        {results.map((res,idx) =>
                        res.type === 'single' ? 
                        <SingleResult res={res} idx={idx}/>
                        :
                        <div className={styles.resultBlock}>
                            <div className={styles.inputEcho}>
                                <span>Threshold {res.testList[0].result.threshold}</span>
                                <hr/>
                                <span>Batch ID {res.batchId}</span>
                            </div>
                          <div className={styles.batchWrap}>
                              <BarChart className={styles.batchChart} data={res.accuracy} />
                              <Confusion className={styles.batchChart} data={res.confusion}/>
                          </div>
                        </div>
                        )}
                        {isRunning&&<OutputSkeleton/>}
                    </div>
                </div>
            </div>
            {isBatchUpload&&<BatchUpload onClose={()=>setIsBatchUpload(false)} onChange={handleRefreshlBatch}/>}
        </div>
    );
};